import { CampaignCard } from '@/components/CampaignCardVertical';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { useMaterialEditStore } from '@/screens/MaterialEdit/store';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

interface CurrentCampaignsProps {
  currentCampaign?: CampaignDto | null;
  defaultCampaign?: CampaignDto | null;
  scheduledCampaigns: {
    campaign: CampaignDto;
    isPaused?: boolean;
    startDate?: string;
    endDate?: string;
    thumbnailUrl?: string;
  }[];
}

export function CurrentCampaigns({
  currentCampaign = null,
  defaultCampaign,
  scheduledCampaigns,
}: CurrentCampaignsProps) {
  const { setOpenModal } = useMaterialEditStore();
  const [showPreview, setShowPreview] = useState(false);
  const [daysLeft, setDaysLeft] = useState('');
  const [expirationDate, setExpirationDate] = useState<string | null>(null);

  // If no scheduled campaign is active, show default campaign as "currently playing"
  const displayCampaign = currentCampaign ?? defaultCampaign;
  const isScheduledCampaignActive = currentCampaign !== null;

  useEffect(() => {
    if (isScheduledCampaignActive && currentCampaign?.uuid) {
      const scheduled = scheduledCampaigns.find((scheduled) => scheduled?.campaign?.uuid === currentCampaign.uuid);
      if (scheduled && scheduled.endDate) {
        const endDate = dayjs(scheduled.endDate);
        // Validate that the parsed date is valid
        if (endDate.isValid()) {
          const today = dayjs();
          const diffDays = endDate.diff(today, 'day');
          setDaysLeft(diffDays ? String(diffDays) : today.isBefore(endDate) ? '1' : '0');
          setExpirationDate(endDate.format('DD/MM/YYYY'));
        } else {
          // Reset to empty if date is invalid
          setDaysLeft('');
          setExpirationDate(null);
        }
      } else {
        // No end date - reset expiration display
        setDaysLeft('');
        setExpirationDate(null);
      }
    } else {
      // Not a scheduled campaign - reset expiration display
      setDaysLeft('');
      setExpirationDate(null);
    }
  }, [currentCampaign, isScheduledCampaignActive, scheduledCampaigns]);

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex flex-row items-center gap-2">
        <Icon name="auto_read_play" fill={1} size={20} className="text-primary" />
        <h3 className="text-md leading-5 font-semibold sm:text-xl">Passando agora</h3>
      </div>
      <div className="border-primary mt-2 flex w-full min-w-[280px] rounded-[5px] border-2 bg-[var(--primary)]/15 p-3 sm:max-w-[400px]">
        <div className="max-w-[100px] sm:max-w-[115px] md:max-w-[125px]">
          {displayCampaign && (
            <CampaignCard
              showExpiration
              showTitle={false}
              campaign={displayCampaign}
              key={displayCampaign.uuid}
              showScheduled
              showPreview={showPreview}
              actions={{
                onClick: () => setShowPreview(true),
                onCloseModal: () => setShowPreview(false),
              }}
            />
          )}
        </div>
        <div className="flex w-full flex-1 flex-col justify-between gap-1 px-2">
          <div className="font-medium">
            <strong className="text-md">{displayCampaign?.name}</strong>
          </div>
          {isScheduledCampaignActive ? (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between text-xs">
                <span>Expira em:</span>{' '}
                <span>
                  {Number(daysLeft) > 1 ? `${daysLeft} dia(s)` : Number(daysLeft) === 1 ? 'Amanhã' : 'Expirado'}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Validade:</span> <span>{expirationDate}</span>
              </div>
            </div>
          ) : (
            <div>
              <span>Veiculação padrão em andamento.</span>
            </div>
          )}
          <RestrictedComponent requiredScopes={['material:campaign']} behavior="hide">
            {isScheduledCampaignActive ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setOpenModal({
                    type: 'play_now',
                    title: 'Trocar campanha',
                    icon: 'change_circle',
                  });
                }}
              >
                <Icon name="change_circle" fill={1} />
                <span className="text-xs sm:text-sm">Trocar campanha</span>
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setOpenModal({
                    type: 'scheduled',
                    title: 'Agendar campanha',
                    icon: 'calendar_add_on',
                  });
                }}
              >
                <Icon name="change_circle" fill={1} />
                <span className="text-xs sm:text-sm">Agendar nova campanha</span>
              </Button>
            )}
          </RestrictedComponent>
        </div>
      </div>
    </div>
  );
}
